/**
 * Answering-machine engine (ADR-042): which detector decides, for every call
 * of the platform. A super admin chooses between the transcript + Jev (as
 * before) and the audio model trained in Swoft AI Studio, served by
 * swoft-classify. Stored in AdminSettings under `amd_engine` through
 * GET/PUT /api/admin/settings/amd_engine, which masks the API key.
 *
 * W6 (B3-AdminAmdEngine, QW-h6):
 *   - shadcn RadioGroup / Checkbox: the chosen engine and the boxes are
 *     visible (native controls rendered as filled black shapes);
 *   - a health panel on top: the engine in force (an incomplete Swoft model
 *     falls back to the transcript, said with its reason), last change,
 *     samples kept, verdicts per detector over 7 days
 *     (GET /api/admin/platform/amd-health);
 *   - internal words (Jev, S3) explained where they appear;
 *   - the storage warning only once recording is asked for;
 *   - saving is reversible: « Annuler » in the toast restores the previous
 *     value (DES-13), no confirmation.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, ApiErrorException } from '@/api/client';
import { API_BASE } from '@/api/api-client';
import { getAmdHealth, type AmdHealth } from '@/api/endpoints/platform';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VFStatCard } from '@/components/vf/StatCard';
import { Checkbox, ErrorLine, PlatformPageHeader, RadioGroup, RadioGroupItem } from '@/components/backoffice/platform/PlatformBits';
import { currentLocale } from '@/i18n/locale';

type Engine = 'transcript_jev' | 'swoft_model';

export interface AmdEngineValue {
  engine: Engine;
  serviceUrl: string;
  modelReference: string;
  apiKey: string;
  recordEnabled: boolean;
  recordSeconds: number;
  retentionDays: number;
  shadow: boolean;
  s3Endpoint: string;
  s3Region: string;
  s3Bucket: string;
  s3AccessKey: string;
  s3SecretKey: string;
}

interface SamplesSummary {
  available: boolean;
  storage: string;
  recording: boolean;
  total: number;
  last7Days: number;
}

const EMPTY: AmdEngineValue = {
  engine: 'transcript_jev',
  serviceUrl: '',
  modelReference: '',
  apiKey: '',
  recordEnabled: false,
  recordSeconds: 20,
  retentionDays: 365,
  shadow: false,
  s3Endpoint: 'https://s3.gra.io.cloud.ovh.net',
  s3Region: 'gra',
  s3Bucket: 'revcall-amd-samples',
  s3AccessKey: '',
  s3SecretKey: '',
};
const S3_FIELDS = ['s3Endpoint', 's3Region', 's3Bucket', 's3AccessKey', 's3SecretKey'] as const;
const SECRET_FIELDS = ['apiKey', 's3SecretKey'] as const;
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
const KEY = 'amd_engine';
const REFERENCE = /^mdl-[a-z0-9]+@\d+$/;

async function readSetting(): Promise<AmdEngineValue> {
  try {
    const s = await apiClient.get<{ value?: Partial<AmdEngineValue> }>(`/admin/settings/${KEY}`);
    return { ...EMPTY, ...(s.value ?? {}) };
  } catch (err) {
    // No setting yet: the server runs on its defaults, shown as such.
    if (err instanceof ApiErrorException && err.status === 404) return EMPTY;
    throw err;
  }
}

function HealthPanel({ health, samples, isError, error, onRetry }: { health?: AmdHealth; samples?: SamplesSummary; isError: boolean; error: unknown; onRetry: () => void }) {
  const { t } = useTranslation(['admin']);
  const state = isError ? 'error' : health ? 'ready' : 'loading';
  const engineLabel = (e: Engine) => t(`admin:amdEngine.${e === 'swoft_model' ? 'swoft' : 'transcript'}.label`);
  const fellBack = health && health.requested_engine !== health.engine;
  return (
    <section aria-labelledby="amd-health-title" className="space-y-3 rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
      <h2 id="amd-health-title" className="text-base font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('admin:amdEngine.health.title')}</h2>
      {isError && <ErrorLine message={t('admin:amdEngine.health.loadError')} error={error} onRetry={onRetry} />}
      {fellBack && (
        <p role="status" className="rounded-vf border px-3 py-2 text-sm" style={{ borderColor: 'var(--vf-orange)', background: 'var(--vf-orange-bg)', color: 'var(--vf-text-1)' }}>
          {t('admin:amdEngine.health.fallback', { requested: engineLabel(health.requested_engine), engine: engineLabel(health.engine), reason: health.fallback_reason })}
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <VFStatCard
          label={t('admin:amdEngine.health.inForce')}
          state={state}
          valueKind="text"
          value={health ? engineLabel(health.engine) : undefined}
          hint={health?.setting_updated_at
            ? t('admin:amdEngine.health.changedAt', { date: new Date(health.setting_updated_at).toLocaleString(currentLocale(), { dateStyle: 'medium', timeStyle: 'short' }) })
            : t('admin:amdEngine.health.neverChanged')}
        />
        <VFStatCard
          label={t('admin:amdEngine.health.decisions', { days: health?.period_days ?? 7 })}
          state={state}
          metric={health ? { current: health.decisions.total, kind: 'count' } : undefined}
        />
        <VFStatCard
          label={t('admin:amdEngine.health.samples')}
          state={samples ? 'ready' : isError ? 'error' : 'loading'}
          valueKind="text"
          value={samples ? (samples.available ? t('admin:amdEngine.health.samplesValue', { total: samples.total, last7Days: samples.last7Days }) : t('admin:amdEngine.health.samplesOff')) : undefined}
        />
      </div>
      {health && health.decisions.by_detector.length > 0 && (
        <div className="overflow-x-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
          <table className="w-full text-[13px]">
            <caption className="sr-only">{t('admin:amdEngine.health.tableCaption', { days: health.period_days })}</caption>
            <thead style={{ background: 'var(--vf-surface-1)' }}>
              <tr>
                {(['detector', 'human', 'machine', 'unknown'] as const).map((c) => (
                  <th key={c} scope="col" className={`h-9 px-3 text-[11px] font-semibold uppercase tracking-wide ${c === 'detector' ? 'text-left' : 'text-right'}`} style={{ color: 'var(--vf-text-3)' }}>
                    {t(`admin:amdEngine.health.columns.${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {health.decisions.by_detector.map((d) => (
                <tr key={d.detector} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                  <td className="h-10 px-3">{t(`admin:amdEngine.health.detectors.${d.detector}`)}</td>
                  <td className="h-10 px-3 text-right tabular-nums">{d.human}</td>
                  <td className="h-10 px-3 text-right tabular-nums">{d.machine}</td>
                  <td className="h-10 px-3 text-right tabular-nums">{d.unknown}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {health && health.decisions.total === 0 && <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:amdEngine.health.noDecision', { days: health.period_days })}</p>}
    </section>
  );
}

export default function AdminAmdEngine() {
  const { t } = useTranslation(['admin', 'common', 'settings']);
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AmdEngineValue>(EMPTY);

  const setting = useQuery({ queryKey: ['admin-setting', KEY], queryFn: readSetting });
  const summary = useQuery({
    queryKey: ['amd-samples-summary'],
    queryFn: () => apiClient.get<SamplesSummary>('/admin/amd-samples/summary'),
  });
  const health = useQuery({ queryKey: ['amd-health'], queryFn: ({ signal }) => getAmdHealth(signal) });
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(today());
  useEffect(() => {
    if (setting.data) setForm(setting.data);
  }, [setting.data]);

  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-setting', KEY] });
    void queryClient.invalidateQueries({ queryKey: ['amd-samples-summary'] });
    void queryClient.invalidateQueries({ queryKey: ['amd-health'] });
  };
  const put = (value: AmdEngineValue) => apiClient.put(`/admin/settings/${KEY}`, { value });
  const save = useMutation({
    mutationFn: async ({ value }: { value: AmdEngineValue; previous: AmdEngineValue }) => put(value),
    onSuccess: (_r, { value, previous }) => {
      refreshAll();
      // REV-W6-F3: the server sends the secrets masked; « Annuler » would put
      // the mask back, which keeps the NEW secret. A changed secret gets no
      // undo, and the toast says why.
      if (SECRET_FIELDS.some((f) => value[f] !== previous[f])) {
        toast.success(t('admin:amdEngine.saved'), { description: t('admin:amdEngine.savedSecretNoUndo') });
        return;
      }
      toast.success(t('admin:amdEngine.saved'), {
        action: {
          label: t('common:actions.undo'),
          onClick: () => {
            put(previous).then(() => {
              refreshAll();
              toast.success(t('admin:amdEngine.restored'));
            }, () => toast.error(t('admin:amdEngine.restoreError')));
          },
        },
      });
    },
  });

  const swoft = form.engine === 'swoft_model';
  const modelReachable = /^https?:\/\//.test(form.serviceUrl) && REFERENCE.test(form.modelReference);
  const incomplete = (swoft || form.shadow) && !modelReachable;
  const storageMissing = form.recordEnabled && summary.data && !summary.data.available && S3_FIELDS.some((f) => !String(form[f] ?? '').trim());

  if (setting.isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" style={{ color: 'var(--vf-accent)' }} aria-label={t('common:actions.loading')} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <PlatformPageHeader title={t('admin:superAdminLayout.nav.amdEngine')} subtitle={t('admin:amdEngine.subtitle')} />

      <HealthPanel health={health.data} samples={summary.data} isError={health.isError} error={health.error} onRetry={() => void health.refetch()} />

      {setting.isError && <ErrorLine message={t('admin:amdEngine.loadError')} error={setting.error} onRetry={() => void setting.refetch()} />}

      <div className="space-y-5 rounded-vf border p-4 sm:p-6" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
        <fieldset className="space-y-3">
          <legend className="font-medium">{t('admin:amdEngine.engineLegend')}</legend>
          <RadioGroup value={form.engine} onValueChange={(engine) => setForm({ ...form, engine: engine as Engine })} className="gap-3">
            {(['transcript_jev', 'swoft_model'] as const).map((engine) => {
              const key = engine === 'swoft_model' ? 'swoft' : 'transcript';
              const selected = form.engine === engine;
              return (
                <label
                  key={engine}
                  htmlFor={`amd-${key}`}
                  className="flex min-h-11 cursor-pointer items-start gap-3 rounded-vf border p-3"
                  style={{ borderColor: selected ? 'var(--vf-accent)' : 'var(--vf-border)', background: selected ? 'var(--vf-accent-bg)' : undefined }}
                >
                  <RadioGroupItem value={engine} id={`amd-${key}`} className="mt-0.5" />
                  <span className="space-y-1">
                    <span className="block font-medium">
                      {t(`admin:amdEngine.${key}.label`)}
                      {selected && <span className="ml-2 text-xs font-normal" style={{ color: 'var(--vf-accent-text)' }}>{t('admin:amdEngine.selected')}</span>}
                    </span>
                    <span className="block text-xs" style={{ color: 'var(--vf-text-2)' }}>{t(`admin:amdEngine.${key}.hint`)}</span>
                  </span>
                </label>
              );
            })}
          </RadioGroup>
        </fieldset>

        {(swoft || form.shadow) && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amd-url">{t('admin:amdEngine.serviceUrl')}</Label>
              <Input id="amd-url" value={form.serviceUrl} onChange={(e) => setForm({ ...form, serviceUrl: e.target.value })} placeholder="http://127.0.0.1:8028" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amd-model">{t('admin:amdEngine.modelReference')}</Label>
              <Input id="amd-model" value={form.modelReference} onChange={(e) => setForm({ ...form, modelReference: e.target.value })} placeholder="mdl-snxk1re5jl@6" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amd-key">{t('admin:amdEngine.apiKey')}</Label>
              <Input id="amd-key" name="amd-service-key" type="password" autoComplete="new-password" value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })} />
            </div>
            {incomplete && <p className="text-xs" style={{ color: 'var(--vf-text-1)' }}>{t('admin:amdEngine.incomplete')}</p>}
          </div>
        )}

        <fieldset className="space-y-3 border-t pt-5" style={{ borderColor: 'var(--vf-border)' }}>
          <legend className="font-medium">{t('admin:amdEngine.samples.title')}</legend>
          <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:amdEngine.samples.hint')}</p>
          <div className="flex min-h-11 items-center gap-3 text-sm">
            <Checkbox id="amd-record" checked={form.recordEnabled} onCheckedChange={(v: boolean | 'indeterminate') => setForm({ ...form, recordEnabled: v === true })} />
            <Label htmlFor="amd-record">{t('admin:amdEngine.samples.record')}</Label>
          </div>
          {form.recordEnabled && (
            <div className="flex flex-wrap gap-4">
              <div className="space-y-2">
                <Label htmlFor="amd-seconds">{t('admin:amdEngine.samples.seconds')}</Label>
                <Input id="amd-seconds" type="number" min={3} max={60} value={form.recordSeconds} onChange={(e) => setForm({ ...form, recordSeconds: Number(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amd-retention">{t('admin:amdEngine.samples.retention')}</Label>
                <Input id="amd-retention" type="number" min={7} max={3650} value={form.retentionDays} onChange={(e) => setForm({ ...form, retentionDays: Number(e.target.value) })} />
              </div>
            </div>
          )}
          {form.recordEnabled && (
            <>
              <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:amdEngine.samples.storageHint')}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {S3_FIELDS.map((f) => (
                  <div key={f} className="space-y-1">
                    <Label htmlFor={`amd-${f}`}>{t(`admin:amdEngine.samples.${f}`)}</Label>
                    <Input id={`amd-${f}`} name={`amd-storage-${f}`} type={f === 's3SecretKey' ? 'password' : 'text'} autoComplete={f === 's3SecretKey' ? 'new-password' : 'off'} value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
                  </div>
                ))}
              </div>
              {storageMissing && <p className="text-xs" style={{ color: 'var(--vf-text-1)' }}>{t('admin:amdEngine.samples.unavailable')}</p>}
            </>
          )}
          {!swoft && (
            <div className="flex min-h-11 items-center gap-3 text-sm">
              <Checkbox id="amd-shadow" checked={form.shadow} onCheckedChange={(v: boolean | 'indeterminate') => setForm({ ...form, shadow: v === true })} />
              <Label htmlFor="amd-shadow">{t('admin:amdEngine.samples.shadow')}</Label>
            </div>
          )}
          {summary.data?.available && (
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-2">
                <Label htmlFor="amd-from">{t('admin:amdEngine.samples.from')}</Label>
                <Input id="amd-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amd-to">{t('admin:amdEngine.samples.to')}</Label>
                <Input id="amd-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <a className="pb-2 text-sm underline" style={{ color: 'var(--vf-accent-text)' }} href={`${API_BASE}/admin/amd-samples/export?from=${from}&to=${to}`}>
                {t('admin:amdEngine.samples.download')}
              </a>
            </div>
          )}
        </fieldset>

        {save.isError && <ErrorLine message={t('admin:amdEngine.saveError')} error={save.error} />}
        <Button onClick={() => save.mutate({ value: form, previous: setting.data ?? EMPTY })} disabled={save.isPending || incomplete || setting.isError}>
          {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" /> : <Save className="mr-2 h-4 w-4" />}
          {save.isPending ? t('common:actions.saving') : t('common:actions.save')}
        </Button>
      </div>
    </div>
  );
}
